import React from 'react';
import { STEPS } from '../data/content';
import useMotionPrefs from '../hooks/useMotionPrefs';
import useScrollVar from '../hooks/useScrollVar';
import ScrollReveal from '../components/ScrollReveal';
import { ArrowRight } from '../components/icons';

/* ==================================================================
   HowItWorks
   ------------------------------------------------------------------
   Desktop: a pinned horizontal filmstrip. The section is 300vh tall,
   its sticky viewport holds a track that slides left by a fixed vw
   distance as --hs-sp goes 0 -> 1. Because the track is measured in
   vw the travel needs no JS measurement, so there is nothing to get
   stale on resize.

   Narrow screens: the same four cards become a vertical timeline,
   each revealing on entry (the horizontal section is not simulated
   with touch-scroll hijacking).
   ================================================================== */

export default function HowItWorks() {
  const { reduced, wide } = useMotionPrefs();
  const pinned = wide && !reduced;
  const ref = useScrollVar({ mode: 'pin', varName: '--hs-sp', disabled: !pinned, resetValue: 0 });

  return (
    <section id="how" className={`hs-how ${pinned ? '' : 'is-static'}`} ref={ref}>
      <div className="hs-how__viewport">
        <header className="hs-how__head">
          <ScrollReveal as="p" className="hs-eyebrow" variant="up">Process</ScrollReveal>
          <ScrollReveal as="h2" className="hs-h2" variant="up" delay={80}>
            How HyrSkill works
          </ScrollReveal>
          <ScrollReveal as="p" className="hs-lede" variant="up" delay={140}>
            Four steps from a student&rsquo;s first search to a company&rsquo;s next hire.
          </ScrollReveal>
        </header>

        <div className="hs-how__rail" aria-hidden="true">
          <span className="hs-how__rail-fill" />
        </div>

        <div className="hs-how__track">
          {STEPS.map((step, i) => (
            <ScrollReveal
              key={step.index}
              className="hs-step-wrap"
              variant="up"
              delay={i * 90}
              style={{ '--hs-i': i }}
            >
              <article className={`hs-step hs-step--${step.side}`}>
                <p className="hs-step__index">{step.index}</p>
                <span className="hs-step__rule" aria-hidden="true" />
                <h3 className="hs-step__title">{step.title}</h3>
                <p className="hs-step__body">{step.body}</p>
                <span className="hs-step__side">
                  {step.side === 'student' ? 'Student' : 'Company'}
                </span>
                {i < STEPS.length - 1 && (
                  <span className="hs-step__next" aria-hidden="true"><ArrowRight size={16} /></span>
                )}
              </article>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}
